import { authedFetch } from '@features/auth/api/authRefresh'
import type { RecoveryResetRequest } from '@/crypto/key-hierarchy.types'
import type {
  CreateGrantResponseWire,
  GrantListItemWire,
  InitKeypairWire,
  ReceivedListItemWire,
  UnlockResponseWire,
} from '../types'

/** takeover 送信 body — granter SK を新マスターパスワードで再封印した暗号材料。 */
export type EmergencyTakeoverBody = RecoveryResetRequest

/**
 * Track F: Emergency Access REST client (KEY_HIERARCHY.md §4.7.5)。
 *
 * 全 13 endpoints (Phase 5 計画 §4):
 *   1. init-keypair             — grantee が RSA-OAEP keypair を upload
 *   2. POST /grants             — granter が invite 作成
 *   3. PATCH /grants/:id/wrap   — granter が wrappedSk を upload
 *   4. GET /grants              — granter が自分の invite 一覧
 *   5. DELETE /grants/:id       — granter が revoke
 *   6. POST /grants/:id/approve — granter が REQUESTED を早期承認
 *   7. POST /grants/:id/deny    — granter が REQUESTED を拒否
 *   8. GET /received            — grantee が自分の invite 一覧
 *   9. POST /received/:id/accept           — grantee が承諾
 *  10. POST /received/:id/decline          — grantee が拒否
 *  11. POST /received/:id/request          — grantee が解錠要求
 *  12. POST /received/:id/cancel-request   — grantee が要求取消
 *  13. POST /received/:id/unlock           — grantee が vault 解錠
 *
 * すべて `authedFetch` 経由 (401 retry あり)。
 */

const BASE = '/users/me/emergency-access'

interface OkResp {
  ok: true
}

export const initEmergencyKeypair = (body: InitKeypairWire): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/init-keypair`, { method: 'POST', body })

export const createGrant = (body: {
  granteeEmail: string
  waitDays: number
  accessType?: 'view' | 'takeover'
}): Promise<CreateGrantResponseWire> =>
  authedFetch<CreateGrantResponseWire>(`${BASE}/grants`, { method: 'POST', body })

export const wrapGrant = (id: string, body: { wrappedSk: string }): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/grants/${encodeURIComponent(id)}/wrap`, {
    method: 'PATCH',
    body,
  })

export const listGrants = (): Promise<GrantListItemWire[]> =>
  authedFetch<GrantListItemWire[]>(`${BASE}/grants`, { method: 'GET' })

export const revokeGrant = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/grants/${encodeURIComponent(id)}`, { method: 'DELETE' })

/** 既存 invite の設定 (待機日数 / アクセス種別) を編集する。INVITED / ACCEPTED のみ可。 */
export const updateGrant = (
  id: string,
  body: { waitDays?: number; accessType?: 'view' | 'takeover' },
): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/grants/${encodeURIComponent(id)}`, { method: 'PATCH', body })

/** 履歴 (REVOKED / DECLINED) の行を物理削除する。granter / grantee 両対応。 */
export const deleteEmergencyHistory = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/history/${encodeURIComponent(id)}`, { method: 'DELETE' })

export const approveGrant = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/grants/${encodeURIComponent(id)}/approve`, { method: 'POST' })

export const denyGrant = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/grants/${encodeURIComponent(id)}/deny`, { method: 'POST' })

export const listReceived = (): Promise<ReceivedListItemWire[]> =>
  authedFetch<ReceivedListItemWire[]>(`${BASE}/received`, { method: 'GET' })

export const acceptReceived = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/received/${encodeURIComponent(id)}/accept`, { method: 'POST' })

export const declineReceived = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/received/${encodeURIComponent(id)}/decline`, { method: 'POST' })

export const requestReceived = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/received/${encodeURIComponent(id)}/request`, { method: 'POST' })

export const cancelReceivedRequest = (id: string): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/received/${encodeURIComponent(id)}/cancel-request`, {
    method: 'POST',
  })

export const unlockReceived = (id: string): Promise<UnlockResponseWire> =>
  authedFetch<UnlockResponseWire>(`${BASE}/received/${encodeURIComponent(id)}/unlock`, {
    method: 'POST',
  })

/** accessType='takeover' の invite で granter のマスターパスワードを再設定する。 */
export const takeoverReceived = (id: string, body: EmergencyTakeoverBody): Promise<OkResp> =>
  authedFetch<OkResp>(`${BASE}/received/${encodeURIComponent(id)}/takeover`, {
    method: 'POST',
    body,
  })
